import { initializeApp, getApps } from 'firebase/app';
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
  Messaging,
} from 'firebase/messaging';
import { firebaseConfig } from './firebaseConfig';
import { getToken as getAuthToken } from './Utils/authUtils';
import { USERS } from './constant';

let messagingPromise: Promise<Messaging | null> | null = null;

/** Lazily init Firebase Messaging; null when the browser can't do web push. */
function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then(ok => {
        if (!ok) {
          return null;
        }
        const app = getApps()[0] || initializeApp(firebaseConfig);
        return getMessaging(app);
      })
      .catch(() => null);
  }
  return messagingPromise;
}

/**
 * Full web-push bootstrap: register the FCM service worker, obtain a token,
 * register it with the backend (stored per-user in fcmWebTokens), and wire the
 * foreground-message callback. Call only after Notification.permission === 'granted'.
 * Returns true when push is live.
 */
export type PushPayload = {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
};

export async function initWebPush(
  onForegroundMessage: (payload: PushPayload) => void
): Promise<boolean> {
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging || !('serviceWorker' in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    // No explicit vapidKey: the SDK uses the project's default FCM key pair.
    // Swap in a Web Push certificate here if/when one is generated in console.
    const token = await getToken(messaging, { serviceWorkerRegistration: registration });
    if (!token) {
      return false;
    }

    const res = await fetch(USERS.WEB_PUSH_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + getAuthToken(),
      },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      console.warn('[webpush] backend token registration failed:', res.status);
    }

    onMessage(messaging, onForegroundMessage);
    return true;
  } catch (err) {
    console.warn('[webpush] init failed:', (err as Error)?.message);
    return false;
  }
}

/**
 * Detach this browser from the CURRENT login's pushes — call on logout and on
 * clinic switch, BEFORE auth storage is cleared. Notifications are scoped to
 * the per-clinic user account; without this a shared browser keeps receiving
 * the previous account's pushes. deleteToken() invalidates the token at FCM
 * (so even stale server copies stop delivering); the backend call prunes it
 * from the user doc eagerly.
 */
export async function teardownWebPush(authTokenOverride?: string): Promise<void> {
  // Caller (logout flow) passes the auth token captured before storage clear;
  // direct calls while still logged in can rely on the storage read.
  const authToken = authTokenOverride || getAuthToken();
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging || !('serviceWorker' in navigator)) {
      return;
    }
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (!registration) {
      return;
    }
    let token = '';
    try {
      token = await getToken(messaging, { serviceWorkerRegistration: registration });
    } catch (_) {
      /* no token to remove */
    }
    if (token && authToken) {
      await fetch(USERS.WEB_PUSH_TOKEN_REMOVE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authToken,
        },
        body: JSON.stringify({ token }),
      }).catch(() => undefined);
    }
    await deleteToken(messaging).catch(() => undefined);
  } catch (err) {
    console.warn('[webpush] teardown failed:', (err as Error)?.message);
  }
}
